import { useState } from 'react';
import type { Id } from '@contracts/common';
import type { BoardItem } from '@contracts/workplace';
import { Combobox, type ComboOption } from '@/components/ui/combobox';
import { SELECTED_OPTION } from '@/components/ui/segment';
import { recentParentsKey } from '@/lib/person-storage';
import { usePrincipal } from '../../api/query';
import { normalizeSearch, type Forest } from './model';

/**
 * The tree parent picker (T2): one searchable list for "Thuộc" in "Việc mới" and for "Chuyển tới…" in the card
 * modal, on the same combobox as the recipient. The first row makes the card a big job of its own; "Dùng gần đây"
 * holds the last three parents this person picked on this browser; the tree follows, 16 px per level. Every row is named by its
 * path ("Chốt kế hoạch tuyển dụng › Phỏng vấn vòng 2"), shown on a search result and a recent row, and read out on
 * a tree row, whose indent already shows it. Notes hold no work, so they are never offered.
 */

export const ROOT_ROW = '— Không thuộc việc nào (thành việc lớn)';
const RECENT_SHOWN = 3;
const RECENT_KEPT = 10;

/**
 * This person's recent parents on this browser, newest first (kept per person, as the view is: authz audit D-F4);
 * no person, blocked storage or junk is an empty list.
 */
export function readRecentParents(userId: Id | undefined): Id[] {
  if (userId === undefined) return [];
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(recentParentsKey(userId)) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((entry): entry is Id => typeof entry === 'string') : [];
  } catch {
    return [];
  }
}

export function rememberParent(userId: Id | undefined, id: Id): void {
  if (userId === undefined) return;
  try {
    const next = [id, ...readRecentParents(userId).filter((entry) => entry !== id)].slice(0, RECENT_KEPT);
    window.localStorage.setItem(recentParentsKey(userId), JSON.stringify(next));
  } catch {
    // A private window or full storage: the list is a convenience, never a fault.
  }
}

/**
 * The home a card lives in (sharing spec §8.4), read from its tree's top card on the board: a root in a project lives in
 * the project's home, shared by every root of that project; any other root is a home of its own. A top card that still
 * has a parent is a fragment of a tree the board does not hold whole (a filtered board): only its own tree is sure to
 * share its home.
 */
export function homeOf(forest: Forest, id: Id): string {
  const node = forest.byId.get(id);
  if (node === undefined) return `item:${id}`;
  const top = forest.byId.get(node.rootId)?.item ?? node.item;
  if (top.parentId !== null) return `fragment:${top.id}`;
  return top.project === null ? `item:${top.id}` : `project:${top.project.id}`;
}

/** "A › B › " — the titles above a card, each followed by the separator. */
function pathOf(forest: Forest, id: Id): string {
  const titles: string[] = [];
  for (let up = forest.byId.get(id)?.parent ?? null; up !== null; up = up.parent) titles.unshift(up.item.title);
  return titles.map((title) => `${title} › `).join('');
}

export function parentOptions({ forest, query, exclude, allow, current, recent, offerRoot }: {
  forest: Forest;
  query: string;
  /** Cards that cannot be the parent: the one being moved and everything under it. */
  exclude?: ReadonlySet<Id>;
  allow?: (item: BoardItem) => boolean;
  /** The parent now ('' for none). */
  current: Id;
  recent: readonly Id[];
  offerRoot: boolean;
}): ComboOption[] {
  const offered = forest.order.filter((item) => item.itemType !== 'note' && !exclude?.has(item.id) && (allow?.(item) ?? true));
  const wanted = normalizeSearch(query);
  const row = (item: BoardItem, key: string, flat: boolean, group?: string): ComboOption => {
    const path = pathOf(forest, item.id);
    return {
      key, value: item.id, group, current: item.id === current,
      className: item.id === current ? SELECTED_OPTION : undefined,
      ...(flat
        ? { path, label: item.title }
        : { depth: forest.byId.get(item.id)?.depth ?? 0, label: <>{path && <span className="sr-only">{path}</span>}{item.title}</> }),
    };
  };
  if (wanted !== '') {
    return offered
      .filter((item) => normalizeSearch(`${pathOf(forest, item.id)}${item.title}`).includes(wanted))
      .map((item) => row(item, item.id, true));
  }
  const out: ComboOption[] = [];
  if (offerRoot) out.push({ key: 'root', value: '', label: ROOT_ROW, current: current === '' });
  const byId = new Map(offered.map((item) => [item.id, item]));
  recent.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : [])).slice(0, RECENT_SHOWN)
    .forEach((item) => out.push(row(item, `recent-${item.id}`, true, 'Dùng gần đây')));
  offered.forEach((item) => out.push(row(item, item.id, false, 'Tất cả')));
  return out;
}

/** The picker itself; `onChoose` gets the new parent, `null` for "no parent". */
export function ParentCombobox({
  forest, current, exclude, allow, offerRoot, onChoose, label = 'Thuộc', autoFocus = true,
}: {
  forest: Forest;
  current: Id;
  exclude?: ReadonlySet<Id>;
  allow?: (item: BoardItem) => boolean;
  offerRoot: boolean;
  onChoose: (parentId: Id | null) => void;
  label?: string;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState('');
  // Read once per opening: a pick made now shows next time, not by reshuffling the list under the pointer.
  const userId = usePrincipal().data?.userId;
  const [recent] = useState(() => readRecentParents(userId));
  const options = parentOptions({ forest, query, exclude, allow, current, recent, offerRoot });
  return (
    <Combobox
      label={label} placeholder="Tìm việc lớn hoặc việc cha…" query={query} onQueryChange={setQuery}
      options={options} autoFocus={autoFocus} empty="Không có việc nào khớp"
      onChoose={(value) => {
        if (value !== '') rememberParent(userId, value);
        onChoose(value === '' ? null : value);
      }}
    />
  );
}
